/**
 * The order a reader walks a flow map in: along the arrows, and among steps
 * that are free at the same moment, the one higher up (TB) or further left
 * (LR) first. The table view lists rows in this order and the auto layout
 * reuses the same sort for the children inside a group.
 *
 * Pure and deterministic: positions and the document's own node order are the
 * only tie-breakers.
 */
import type { FlowMapDoc } from "../model.js";
import { nodeBoxes } from "./geometry.js";

/**
 * Kahn's topological sort with a priority: among the nodes whose incoming
 * arrows are all resolved, `before` decides which comes first. On a cycle
 * (nothing is ready but nodes remain) the node with the fewest unresolved
 * incoming arrows is released, ties again by `before`. Arrows whose ends are
 * not both in `ids` are ignored. Deterministic for a deterministic `before`.
 */
export function kahn(ids: readonly string[], edges: ReadonlyArray<readonly [string, string]>, before: (a: string, b: string) => number): string[] {
  const inSet = new Set(ids);
  const succ = new Map<string, string[]>();
  const indeg = new Map<string, number>();
  for (const id of ids) {
    succ.set(id, []);
    indeg.set(id, 0);
  }
  for (const [s, t] of edges) {
    if (s === t || !inSet.has(s) || !inSet.has(t)) continue;
    succ.get(s)!.push(t);
    indeg.set(t, indeg.get(t)! + 1);
  }
  const done = new Set<string>();
  const out: string[] = [];
  const ready = ids.filter((id) => indeg.get(id) === 0);
  while (out.length < ids.length) {
    if (!ready.length) {
      let pick: string | undefined;
      for (const id of ids) {
        if (done.has(id)) continue;
        if (pick === undefined) pick = id;
        else {
          const diff = indeg.get(id)! - indeg.get(pick)!;
          if (diff < 0 || (diff === 0 && before(id, pick) < 0)) pick = id;
        }
      }
      if (pick === undefined) break;
      ready.push(pick);
    }
    let bi = 0;
    for (let i = 1; i < ready.length; i++) if (before(ready[i]!, ready[bi]!) < 0) bi = i;
    const id = ready.splice(bi, 1)[0]!;
    if (done.has(id)) continue;
    done.add(id);
    out.push(id);
    for (const t of succ.get(id)!) {
      if (done.has(t)) continue;
      const left = indeg.get(t)! - 1;
      indeg.set(t, left);
      if (left === 0) ready.push(t);
    }
  }
  return out;
}

/**
 * Every node id in reading order. Non-group nodes are sorted along the arrows
 * (absolute positions break ties: (y, x) for TB, (x, y) for LR); each group is
 * placed right before its first child, and empty groups go last.
 *
 * Positions are compared by box CENTRE, so a short start pill and a tall note
 * centred on the same row (what the auto layout produces) read left to right
 * instead of the taller box jumping ahead because its top edge is higher.
 */
export function flowOrder(doc: FlowMapDoc): string[] {
  const lr = doc.direction === "LR";
  const centres = new Map<string, { x: number; y: number }>();
  for (const [id, b] of nodeBoxes(doc)) centres.set(id, { x: b.x + b.width / 2, y: b.y + b.height / 2 });
  const index = new Map(doc.nodes.map((n, i) => [n.id, i] as const));
  const before = (a: string, b: string): number => {
    const pa = centres.get(a)!;
    const pb = centres.get(b)!;
    const main = lr ? pa.x - pb.x : pa.y - pb.y;
    if (main) return main;
    const cross = lr ? pa.y - pb.y : pa.x - pb.x;
    return cross || index.get(a)! - index.get(b)!;
  };
  const kindById = new Map(doc.nodes.map((n) => [n.id, n.kind] as const));
  const plain = doc.nodes.filter((n) => n.kind !== "group").map((n) => n.id);
  const order = kahn(
    plain,
    doc.edges.map((e) => [e.source, e.target] as const),
    before,
  );

  const groupAt = new Map<number, string>();
  const placed = new Set<string>();
  const parentOf = new Map(doc.nodes.map((n) => [n.id, n.parentId] as const));
  order.forEach((id, i) => {
    const g = parentOf.get(id);
    if (g && kindById.get(g) === "group" && !placed.has(g)) {
      placed.add(g);
      groupAt.set(i, g);
    }
  });
  const out: string[] = [];
  order.forEach((id, i) => {
    const g = groupAt.get(i);
    if (g) out.push(g);
    out.push(id);
  });
  const empty = doc.nodes.filter((n) => n.kind === "group" && !placed.has(n.id)).map((n) => n.id);
  empty.sort(before);
  return out.concat(empty);
}
